import React, { useRef, useEffect, useState } from 'react';
import { motion } from "framer-motion";

const AudioVisualizer = ({ src, onEnded, onTimeUpdate, variant = 'bars', icon, paused }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  
  // Refs for manipulation
  const barsRef = useRef([]);
  const iconRef = useRef(null);
  const ring1Ref = useRef(null);
  const ring2Ref = useRef(null);
  const prevHeightsRef = useRef([20, 20, 20, 20, 20]);

  // Handle external pause control
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (paused) {
      audio.pause();
    } else {
      // Only play if it was supposed to be playing (we might need more complex state if we want to resume only if it was playing)
      // For this specific use case (auto-play slide), we generally want to resume.
      // However, we should check if the audio has ended or hasn't started to avoid errors.
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
            // Auto-play might be blocked or audio not ready, just log it
            // console.log("Resume failed", e); 
        });
      }
    }
  }, [paused]);

  useEffect(() => {
    // ... (audio setup remains same)
    const audio = audioRef.current;
    if (!audio) return;
    
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
        if (onTimeUpdate) {
            onTimeUpdate(audio.currentTime, audio.duration);
        }
    };
    const handleEnded = () => {
        setIsPlaying(false);
        if (onEnded) onEnded();
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    const setupAudioContext = () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        if (!analyserRef.current) {
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 64;
          analyserRef.current.smoothingTimeConstant = 0.8;
        }
        
        if (!sourceRef.current) {
          sourceRef.current = audioContextRef.current.createMediaElementSource(audio);
          sourceRef.current.connect(analyserRef.current);
          analyserRef.current.connect(audioContextRef.current.destination);
        }
      } catch (e) {
        console.log("Web Audio API setup failed:", e);
      }
    };

    setupAudioContext();

    // Initial play (only if not paused initially)
    if (!paused) {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
        playPromise
            .then(() => {
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }
            })
            .catch(e => console.log("Autoplay failed", e));
        }
    }

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [src, onEnded, onTimeUpdate]); // Removed paused from dependency to avoid re-running setup

  // Animate visualizer based on audio data
  useEffect(() => {
    if (!isPlaying || !analyserRef.current) {
      if (variant === 'bars') {
          barsRef.current.forEach(bar => {
            if (bar) bar.style.height = '20%';
          });
      } else if (variant === 'microphone') {
          if (iconRef.current) iconRef.current.style.transform = 'scale(1)';
          if (ring1Ref.current) ring1Ref.current.style.transform = 'scale(1)';
          if (ring2Ref.current) ring2Ref.current.style.transform = 'scale(1)';
      }
      return;
    }

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const DECAY = 0.92;

    const updateVisualizer = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      
      if (variant === 'bars') {
          const rawValues = [
            dataArray[1] || 0,
            dataArray[3] || 0,
            dataArray[5] || 0,
            dataArray[7] || 0,
            dataArray[9] || 0
          ];
          
          rawValues.forEach((val, i) => {
            const barElement = barsRef.current[i];
            if (!barElement) return;

            const targetHeight = Math.max(20, (val / 255) * 100);
            const prevHeight = prevHeightsRef.current[i];

            // Smooth interpolation (lerp) for both rising and falling
            const smoothingFactor = 0.3; // Lower = smoother but slower response
            let newHeight = prevHeight + (targetHeight - prevHeight) * smoothingFactor;
            
            newHeight = Math.max(20, newHeight);
            barElement.style.height = `${newHeight}%`;
            prevHeightsRef.current[i] = newHeight;
          });
      } else if (variant === 'microphone' && iconRef.current) {
          let sum = 0;
          const relevantBins = Math.min(bufferLength, 16); 
          for(let i = 0; i < relevantBins; i++) {
              sum += dataArray[i];
          }
          const average = sum / relevantBins;
          const normalizedVolume = average / 255;
          
          // Icon: subtle pulse (solid core)
          iconRef.current.style.transform = `scale(${1 + normalizedVolume * 0.15})`;
          
          // Inner Ring: medium expansion
          if (ring1Ref.current) ring1Ref.current.style.transform = `scale(${1 + normalizedVolume * 0.4})`;
          
          // Outer Ring: large expansion (most reactive)
          if (ring2Ref.current) ring2Ref.current.style.transform = `scale(${1 + normalizedVolume * 0.8})`;
          
          // Update background circles (for blue ripple variant)
          const reactivityLevels = [0.15, 0.25, 0.35, 0.5, 0.7, 1.0];
          for (let i = 0; i < 6; i++) {
            const circleEl = document.getElementById(`audio-circle-${i}`);
            if (circleEl) {
              const scale = 1 + (normalizedVolume * reactivityLevels[i]);
              circleEl.style.transform = `scale(${scale})`;
            }
          }
      }
      
      animationRef.current = requestAnimationFrame(updateVisualizer);
    };

    updateVisualizer();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, variant]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center w-full max-w-md">
      {/* Visualizer Container */}
      <div className="flex items-center justify-center h-40 w-full">
        {variant === 'bars' ? (
            <div className="flex items-center justify-center gap-2 h-24">
                {[0, 1, 2, 3, 4].map((i) => (
                    <div 
                        key={i}
                        ref={el => barsRef.current[i] = el}
                        className="w-1.5 bg-white rounded-full will-change-[height]"
                        style={{ height: '20%' }}
                    />
                ))}
            </div>
        ) : (
            <div className="relative flex items-center justify-center w-32 h-32">
                {/* Synced Rings */}
                <div 
                    ref={ring2Ref}
                    className="absolute inset-0 rounded-full bg-white/5 will-change-transform transition-transform duration-75"
                />
                <div 
                    ref={ring1Ref}
                    className="absolute inset-4 rounded-full bg-white/10 will-change-transform transition-transform duration-75"
                />
                
                {/* Main Icon (Bigger, No Shadow) */}
                <div 
                    ref={iconRef}
                    className="relative z-10 w-20 h-20 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-md will-change-transform transition-transform duration-75"
                >
                    <img src={icon} alt="Microphone" className="w-8 h-8 invert opacity-90" />
                </div>
            </div>
        )}
      </div>
      
      <audio 
        ref={audioRef} 
        src={src} 
        crossOrigin="anonymous"
        className="hidden" 
      />
    </div>
  );
};

export default AudioVisualizer;
